import { z } from 'zod';
import type {
  MigrationPlan,
  MigrationRecord,
} from '../../../../functions/src/monitoring/unified/migrationModel';
import { ruleDefinitionSchema } from './contracts';

export type { MigrationPlan, MigrationRecord };
const identity = z.string().min(1);
const timestamp = z.number().finite().nonnegative();
const recipientSchema = z.object({
  to: z.array(z.string().email()),
  bcc: z.array(z.string().email()),
  appUserIds: z.array(identity),
  pushUserIds: z.array(identity),
  smsPhones: z.array(identity),
  smsText: z.string().optional(),
});
const planFields = {
  origin: z.literal('native').optional(),
  id: identity,
  planId: identity,
  algorithmVersion: z.literal(1),
  orgId: identity,
  sensorId: identity,
  sensorType: identity,
  deviceId: z.string(),
  sourceFingerprint: identity,
  sources: z.array(
    z.object({
      ref: identity,
      store: z.enum(['firestore', 'rtdb']),
      fingerprint: identity,
      updateVersion: z.string().nullable(),
    })
  ),
  ruleMappings: z.array(
    z.object({
      ruleId: identity,
      sourceRuleId: identity,
      desiredRule: ruleDefinitionSchema,
      recipients: recipientSchema,
      sourceRefs: z.array(z.string()),
      ruleVersion: z.number().int().positive(),
      activationBaseline: z.record(z.unknown()).optional(),
    })
  ),
  blockers: z.array(
    z.object({
      code: identity,
      stage: z.enum(['prepare', 'activation']),
      message: z.string(),
      sourceRefs: z.array(z.string()),
      sourceRuleId: z.string().optional(),
    })
  ),
  notes: z.array(z.string()),
};

export const migrationPlanSchema: z.ZodType<MigrationPlan> = z.object(planFields);
export const migrationRecordSchema: z.ZodType<MigrationRecord> = z
  .object({
    ...planFields,
    status: z.enum(['prepared', 'active']),
    version: z.number().int().positive(),
    preparedAt: timestamp,
    preparedBy: identity,
    activatedAt: timestamp.nullable(),
    activatedBy: z.string().nullable(),
  })
  .refine(
    (record) =>
      record.status === 'active'
        ? record.activatedAt !== null && Boolean(record.activatedBy?.trim())
        : record.activatedAt === null && record.activatedBy === null,
    'Alarmopsætningens aktivering kunne ikke bekræftes.'
  );
export const migrationOverviewSchema = z.object({
  plan: migrationPlanSchema.nullable(),
  migration: migrationRecordSchema.nullable(),
});
export const migrationSaveSchema = z.object({ migration: migrationRecordSchema });
export type MigrationOverview = z.infer<typeof migrationOverviewSchema>;
